/// FOURJS_START_COPYRIGHT(D,2019)
/// Property of Four Js*
/// (c) Copyright Four Js 2019, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("RTableWidget", ["TableWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * Responsive table widget.
   * @class RTableWidget
   * @memberOf classes
   * @extends classes.TableWidgetBase
   * @publicdoc
   */
  cls.RTableWidget = context.oo.Class(cls.TableWidgetBase, function ($super) {
    return /** @lends classes.RTableWidget.prototype */ {
      $static: /** @lends classes.RTableWidget */ {
        /**
         * Table widget which has a client items selection
         * @type {classes.RTableWidget}
         */
        _tableWithItemsSelection: null,

        /**
         * Store Table widget which has a client items selection
         * @param {classes.RTableWidget} table
         */
        setTableWithItemsSelection: function (table) {
          this._tableWithItemsSelection = table;
        },
        /**
         * Get Table widget which has a client items selection
         * @returns {classes.RTableWidget}
         */
        getTableWithItemsSelection: function () {
          return this._tableWithItemsSelection;
        }
      },

      __name: "RTableWidget",

      /** @type {classes.ContextMenuWidget} */
      _contextMenu: null,

      /** @type {classes.TableConfigSidePanelWidget} */
      _configSidePanel: null,

      /** @type {HTMLElement} */
      _editingTagElement: null,

      /** @type {classes.RTableRowWidget} */
      _headerRowWidget: null,
      /** @type {classes.RTableRowWidget} */
      _footerAggregatesRowWidget: null,

      /** @type {boolean} */
      _multiRowSelectionEnabled: false,

      // Pagination nav WIP
      /** @type {classes.PaginationWidget} */
      //_paginationWidget: null,

      /**
       * Store all the columns
       * @type {classes.RTableColumnWidget[]}
       */
      _columns: null,

      /** @type {classes.RTableColumnWidget[]} */
      _orderedColumns: null,

      /**
       * Current sort rules in priority order, as parsed from the sortSpec attribute
       * @type {?Array<{colName: string, colIndex: number, order: string, kind: string, position: number}>}
       */
      _sortSpec: null,

      /** @type {boolean} */
      _hasFooter: false,

      /** @type {boolean} */
      _firstLayout: true,

      /**
       * Current Column index
       * @type {number}
       */
      _currentColumn: null,

      /** DOM Elements */
      /** @type {Element} */
      _headerGroupElement: null,
      /** @type {Element} */
      _footerGroupElement: null,
      /** @type {Element} */
      _scrollerXElement: null,
      /** @type {Element} */
      _scrollerYElement: null,
      /** @type {HTMLDivElement} */
      _aggregateGlobalTextElement: null,

      /** Item client selection */
      _defaultItemSelection: false,
      _firstItemSelected: null,
      _itemSelectionInProgress: false,
      _itemSelectionHasChanged: false,
      /** @type {HTMLSpanElement} */
      _itemSelectionElement: null,

      /** @type {?number} */
      lastSentOffset: null,

      /** @type {number} */
      _previousScrollLeftValue: -1,
      /** @type {number} */
      _previousScrollTopValue: 0,
      /** @type {number|null} */
      _previousTouchYValue: null,
      /** @type {number} */
      _totalLeftFrozenColumnWidth: 0,
      /** @type {number} */
      _totalVisibleLeftFrozenColumns: 0,
      /** @type {boolean} */
      _frozenTable: false,
      /** @type {number} */
      _leftFrozenColumns: 0,
      /** @type {number} */
      _rightFrozenColumns: 0,

      /** @type {String} */
      _evenRowBackgroundColor: "",
      /** @type {String} */
      _oddRowBackgroundColor: "",

      /** @type {String} */
      _viewType: "", // Type of table view

      // All views: variables/settings
      /** @type {boolean|null} */
      _alternateRows: null,
      /** @type {boolean|null} */
      _rowHover: null,

      // 4st styles
      /** @type {boolean|null} */
      _showGridX: null,
      /** @type {boolean|null} */
      _showGridY: null,
      /** @type {boolean|null} */
      _headerHidden: null,
      /** @type {String} */
      _headerAlignment: null,
      /** @type {String} */
      _headerPosition: null,

      /** Mouse down target html element */
      _mouseDownTarget: null,

      /** @type {classes.RTableItemWidget} */
      _currentItem: null,

      /** @type {Boolean} **/
      _isScrolling: false,

      /** @type {?number} **/
      _vmCurrentRow: null,

      // Store mouse move prev positions
      _itemSelectionMouseMovePrevX: 0,
      _itemSelectionMouseMovePrevY: 0,

      /** @type {boolean} */
      _cancelMenuButtonForThisTouchPhase: false,

      /**
       * @type {classes.TableMenuButtonWidgetComponent}
       */
      _menuButtonComponent: null,

      /**
       * @type {classes.ColumnManipulationWidgetComponent}
       */
      _columnContainerComponent: null,

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._headerRowWidget) {
          this._headerRowWidget.destroy();
          this._headerRowWidget = null;
        }

        if (this._footerAggregatesRowWidget) {
          this._footerAggregatesRowWidget.destroy();
          this._footerAggregatesRowWidget = null;
        }

        if (this._contextMenu) {
          this._contextMenu.destroyChildren();
          this._contextMenu.destroy();
          this._contextMenu = null;
        }

        if (this._configSidePanel) {
          this._configSidePanel.destroy();
          this._configSidePanel = null;
        }

        if (this._editingTagElement) {
          this._editingTagElement.remove();
          this._editingTagElement = null;
        }

        this._sortSpec = null;

        if (this._cachedDataModel) {
          this._cachedDataModel.destroy();
          this._cachedDataModel = null;
        }

        if (this._menuButtonComponent) {
          this._menuButtonComponent.destroy();
          this._menuButtonComponent = null;
        }

        if (this._columnContainerComponent) {
          this._columnContainerComponent.destroy();
          this._columnContainerComponent = null;
        }

        // Pagination nav WIP
        //if (this._paginationWidget) {
        //  this._paginationWidget.destroy();
        //  this._paginationWidget = null;
        //}

        this.setDndItemEnabled(false);

        cls.DOMElementHelper.off(this.getScrollableArea(), "scroll.RTableWidget");

        // Mouse drag
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "dragstart.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "dragend.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "drag.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "dragover.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "drop.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "dragleave.RTableWidget");

        // Touch drag
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "touchstart.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "touchend.RTableWidget");
        cls.DOMElementHelper.off(this.getHeaderGroupElement(), "touchmove.RTableWidget");

        // client select items events
        cls.DOMElementHelper.off(this.getElement(), "mousemove.RTableWidget");
        cls.DOMElementHelper.off(this.getElement(), "mouseleave.RTableWidget");

        cls.DOMElementHelper.off(this.getElement(), "wheel.RTableWidget");
        cls.DOMElementHelper.off(this.getElement(), "touchstart.RTableWidget");
        cls.DOMElementHelper.off(this.getElement(), "touchmove.RTableWidget");

        this._headerGroupElement = null;
        this._footerGroupElement = null;
        this._aggregateGlobalTextElement = null;
        this._scrollerXElement = null;
        this._scrollerYElement = null;

        this._columns = [];
        this._orderedColumns = null;

        this.destroyChildren();
        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);

        this._resetItemsSelection();

        // Pagination nav WIP
        //this._paginationWidget = cls.WidgetFactory.createWidget("Pagination", this.getBuildParameters());
        //this._paginationWidget.setParentWidget(this);
        //this._element.appendChild(this._paginationWidget.getElement());

        this._cachedDataModel = new cls.TableCachedDataModel(this);
        this._columns = [];
        this.setViewType("classic");
        this.setRowHover(!cls.WindowHelper.isMobile());

        if (this.isTreeView()) {
          this.addClasses("gbc_TreeView");
        }

        // set scrollbar size for css rules
        this._element.style.setProperty("--scrollBarHorizontalHeight", window.scrollBarSize + "px");
        this._element.style.setProperty("--scrollBarVerticalWidth", window.scrollBarSize + "px");

        cls.DOMElementHelper.on(this.getElement(), "wheel.RTableWidget", this._onWheel.bind(this));
        cls.DOMElementHelper.on(this.getElement(), "touchstart.RTableWidget", this._onTouchStart.bind(this));
        cls.DOMElementHelper.on(this.getElement(), "touchmove.RTableWidget", cls.FunctionHelper.throttle(this._onTouchMove.bind(this), 10));
      },

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);

        this._columnContainerComponent = new cls.ColumnManipulationWidgetComponent(this);
        this._menuButtonComponent = new cls.TableMenuButtonWidgetComponent(this, true);

        cls.DOMElementHelper.on(this.getScrollableArea(), "scroll.RTableWidget", this._onScroll.bind(this));

        this._headerRowWidget = cls.WidgetFactory.createWidget("RTableRow", this.getBuildParameters());
        this.getHeaderRowWidget().setHeader(true);
        this.getHeaderGroupElement().appendChild(this._headerRowWidget.getElement());
        this.getHeaderRowWidget().setParentWidget(this);
        this.getHeaderRowWidget().onAttachedToParentTable();

        const headerElement = this.getHeaderGroupElement();
        cls.DOMElementHelper.on(headerElement, "dragstart.RTableWidget", this._onHeaderDragStart.bind(this));
        cls.DOMElementHelper.on(headerElement, "dragend.RTableWidget", this._onHeaderDragEnd.bind(this));
        cls.DOMElementHelper.on(headerElement, "drag.RTableWidget", cls.FunctionHelper.throttle(this._onHeaderDrag.bind(this), 5));
        cls.DOMElementHelper.on(headerElement, "dragover.RTableWidget", this._onHeaderDragOver.bind(this));
        cls.DOMElementHelper.on(headerElement, "drop.RTableWidget", this._onHeaderDrop.bind(this));
        cls.DOMElementHelper.on(headerElement, "dragleave.RTableWidget", this._onHeaderDragLeave.bind(this));
        cls.DOMElementHelper.on(headerElement, "touchstart.RTableWidget", this._onHeaderTouchStart.bind(this));
        cls.DOMElementHelper.on(headerElement, "touchend.RTableWidget", this._onHeaderTouchEnd.bind(this));
        cls.DOMElementHelper.on(headerElement, "touchmove.RTableWidget", cls.FunctionHelper.throttle(this._onHeaderTouchMove.bind(this), 5));

        // client select items events
        cls.DOMElementHelper.on(this.getElement(), "mouseleave.RTableWidget", this._onItemMouseLeave.bind(this));

        this._footerAggregatesRowWidget = cls.WidgetFactory.createWidget("RTableRow", this.getBuildParameters());
        this._footerAggregatesRowWidget.setFooter(true);
        this.getFooterGroupElement().appendChild(this._footerAggregatesRowWidget.getElement());
        this._footerAggregatesRowWidget.setParentWidget(this);
      },

      /**
       * @inheritDoc
       */
      _whenParentActivated: function (opt) {
        $super._whenParentActivated.call(this, opt);

        if (!this.isChildOf(opt.data[0])) {
          return;
        }

        this.getScrollableArea().scrollTo(this._previousScrollLeftValue, this._previousScrollTopValue);

        // synchronize header & data horizontal scroll
        this.getHeaderGroupElement().scrollTo(this._previousScrollLeftValue, 0);
        this.getFooterGroupElement().scrollTo(this._previousScrollLeftValue, 0);

        if (!this.getLayoutEngine().isLayoutDone()) {
          this.getLayoutEngine().forceMeasurement();
          this.getLayoutEngine().invalidateMeasure();
        }
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this._layoutInformation) {
          this._layoutInformation = new cls.TableLayoutInformation(this);

          this._layoutInformation.getStretched().setDefaultX(true);
          this._layoutInformation.getStretched().setDefaultY(true);
        }
        this._layoutEngine = new cls.RTableLayoutEngine(this);
        this._layoutEngine.onLayoutApplied(this._layoutApplied.bind(this));

        let minPageSize = Number.parseInt(context.ThemeService.getValue("gbc-TableWidget-min-page-size"), 10);
        this._layoutEngine.setMinPageSize(Number.isNaN(minPageSize) ? 1 : minPageSize);
        let minWidth = Number.parseInt(context.ThemeService.getValue("gbc-TableWidget-min-width"), 10);
        this._layoutEngine.setMinWidth(Number.isNaN(minWidth) ? 60 : minWidth);

        this.setRowHeight(this.getRowHeight());
      },

      /**
       * @inheritDoc
       */
      resetLayout: function () {
        $super.resetLayout.call(this);
        this._firstLayout = true;
        //Refresh alternate rows color in case of theme change
        this.setAlternateRows(this._alternateRows, true, true);
        this._layoutEngine.resetLayout();
      },

      isGridChildrenInParent: function () {
        return false;
      },
      /**
       * Call when layout is finished
       */
      _layoutApplied: function () {
        if (this.isElementInDOM()) {
          const forceColsMeasure = this._firstLayout;
          if (this._firstLayout && this._layoutEngine.isLayoutDone()) {
            // first time layout is applied
            this._firstLayout = false;
            this.updateFrozenColumns();
            this.updateAllAggregate();
            this.updateVerticalScroll(true);
            if (this.getCurrentRow() !== null) {
              this.setCurrentRow(this.getCurrentRow());
            }
            // once measured (needed for columns layout), hide first row if size = 0
            const firstRow = this.getRows()[0];
            if (firstRow) {
              firstRow.setHidden(this.getVisibleRowCount() === 0);
            }
          }
          // add/remove non-visible columns from DOM
          // force columns grid css measure event if no columns are remove/add from DOM on first  layout
          this.updateVisibleColumnsInDom(this.getScrollableArea().scrollLeft, forceColsMeasure);
        }
      },

      /**
       * True if the element is visible
       * @param {DOMElement} element
       * @returns {boolean}
       */
      isElementVisible: function (element) {
        const rect = element.getBoundingClientRect(),
          vWidth = window.innerWidth || document.documentElement.clientWidth,
          vHeight = window.innerHeight || document.documentElement.clientHeight;

        // Return false if it's not in the viewport
        if (rect.right < 0 || rect.bottom < 0 || rect.left > vWidth || rect.top > vHeight) {
          return false;
        }

        // true if any of its four corners are visible
        return (
          element.contains(document.elementFromPoint(Math.ceil(rect.left), Math.ceil(rect.top))) ||
          element.contains(document.elementFromPoint(Math.floor(rect.right), Math.ceil(rect.top))) ||
          element.contains(document.elementFromPoint(Math.floor(rect.right), Math.floor(rect.bottom))) ||
          element.contains(document.elementFromPoint(Math.ceil(rect.left), Math.floor(rect.bottom)))
        );
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        if (this.isElementInDOM()) {
          this.domFocus(fromMouse || preventScroll || this.isElementVisible(this.getElement()));
        } else {
          let uiWidget = this.getUserInterfaceWidget();
          if (uiWidget) {
            this.domFocus();
          }
        }

        $super.setFocus.call(this, fromMouse, preventScroll);

        // if focus comes from VM not mouse update the rowBound actions
        if (!fromMouse) {
          for (const row of this.getRows()) {
            row.updateRowBound();
          }
        }
      },

      /**
       * @inheritDoc
       */
      loseVMFocus: function (vmNewFocusedWidget = null) {
        $super.loseVMFocus.call(this, vmNewFocusedWidget);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        if (this._contextMenu?.isVisible()) {
          return this._contextMenu.managePriorityKeyDown(keyString, domKeyEvent, repeat);
        }

        // manage CTRL+C case
        if (keyString === "ctrl+c" || keyString === "meta+c") {
          if (this.hasItemsSelected()) {
            // copy selection
            this._copySelectionInClipboard();
            keyProcessed = true;
          }
        }

        if (this.isMultiRowSelectionEnabled()) {
          let key = context.KeyboardService.getKeyMap(domKeyEvent.which);
          if (key === "space") {
            this.emit(context.constants.widgetEvents.keySpace, domKeyEvent);
            keyProcessed = true;
          } else if (keyString === "ctrl+a" || keyString === "meta+a") {
            this.emit(context.constants.widgetEvents.selectAll);
            keyProcessed = true;
          } else {
            keyProcessed = this._manageNavigationKeys(domKeyEvent);
          }
        }

        if (keyProcessed) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Manage navigation keys
       * @param {Object} domKeyEvent - key event from DOM
       * @returns {boolean} returns if the domKeyEvent has been processed by the widget
       * @private
       */
      _manageNavigationKeys: function (domKeyEvent) {
        const key = context.KeyboardService.getKeyMap(domKeyEvent.which);
        let keyProcessed = true;
        switch (key) {
          case "down":
            this.emit(context.constants.widgetEvents.keyArrowDown, domKeyEvent);
            break;
          case "up":
            this.emit(context.constants.widgetEvents.keyArrowUp, domKeyEvent);
            break;
          case "pagedown":
            this.emit(context.constants.widgetEvents.keyPageDown, domKeyEvent);
            break;
          case "pageup":
            this.emit(context.constants.widgetEvents.keyPageUp, domKeyEvent);
            break;
          case "home":
            this.emit(context.constants.widgetEvents.keyHome, domKeyEvent);
            break;
          case "end":
            this.emit(context.constants.widgetEvents.keyEnd, domKeyEvent);
            break;
          default:
            keyProcessed = false;
        }

        return keyProcessed;
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        let keyProcessed = false;

        const key = context.KeyboardService.getKeyMap(domKeyEvent.which);

        if (!this.isMultiRowSelectionEnabled() && domKeyEvent) {
          keyProcessed = this._manageNavigationKeys(domKeyEvent);
        }

        // manage CTRL+C case
        // @note is this code really necessary ? there is a VM action "editcopy" which already do the job
        if (keyString === "ctrl+c" || keyString === "meta+c") {
          if (this.isDisplayMode() && !this.isMultiRowSelectionEnabled() && this.isCurrentRowVisible()) {
            // copy current row
            if (this.hasFocusOnField()) {
              this.emit(gbc.constants.widgetEvents.copyCell, this.getItem(this.getCurrentRow(), this.getCurrentColumn()));
              keyProcessed = true;
            } else {
              this.emit(gbc.constants.widgetEvents.copyCurrentRow);
              keyProcessed = true;
            }
          }
        }

        if (!keyProcessed && this.isDisplayMode()) {
          if (key === "left") {
            this.emit(context.constants.widgetEvents.keyArrowLeft, domKeyEvent);
            keyProcessed = true;
          } else if (key === "right") {
            this.emit(context.constants.widgetEvents.keyArrowRight, domKeyEvent);
            keyProcessed = true;
          }
        }

        if (keyProcessed) {
          return true;
        }
        return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      manageLocalAction: function (localActionName) {
        if (localActionName === "prevpage") {
          this.emit(context.constants.widgetEvents.keyPageUp);
        } else if (localActionName === "nextpage") {
          this.emit(context.constants.widgetEvents.keyPageDown);
        }
      },

      /**
       * @inheritDoc
       */
      manageMouseDown: function (domEvent) {
        // clear Table items selection, if not right click on items selection
        if (
          this !== cls.RTableWidget.getTableWithItemsSelection() ||
          domEvent.button !== 2 ||
          !domEvent.target.classList.contains("gbc_RTableWidget__itemSelectionArea")
        ) {
          cls.RTableWidget.getTableWithItemsSelection()?._resetItemsSelection();
        }

        this._onItemMouseDown(domEvent);
        return true;
      },

      /**
       * @inheritDoc
       */
      manageMouseUp: function (domEvent) {
        this._onItemMouseUp(domEvent);
        return true;
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target === null) {
          return true;
        } // just ignore qa click on Table Widget

        if (domEvent.target === this.getContainerElement()) {
          if (domEvent.target === this._mouseDownTarget || !this.isEnabled()) {
            this.emit(context.constants.widgetEvents.requestFocus);
            // Add new row, done by emit(tableClickOnContainer)
            // when clicking the container element and not an item
            let clickedColumnIndex = 0;
            if (this._columns.length > 1) {
              // If there is multiple columns. Get the clicked column
              let orderedCols = this.getOrderedColumns();
              let xClick = domEvent.clientX || domEvent.screenX;
              let counter = 0;
              for (const col of orderedCols) {
                let rect = col.getElement().getBoundingClientRect();
                if (xClick > rect.left && xClick < rect.right) {
                  // If the click is done in a column
                  clickedColumnIndex = counter;
                  continue;
                }
                counter += 1;
              }
            }
            // if click is not after the last item use first column to add new row
            this.emit(context.constants.widgetEvents.tableClickOnContainer, clickedColumnIndex);
            return false;
          }
        } else if (cls.DOMElementHelper.elementOrParent(domEvent.target, cls.TableMenuButtonWidgetComponent._elementCssClass)) {
          this._buildContextMenu(domEvent);
          return false;
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageMouseDblClick: function (domEvent) {
        let target = domEvent.target;

        // click on data
        let isDataContainerClick = cls.DOMElementHelper.isElementOrChildOf(target, this.getContainerElement());

        if (!isDataContainerClick) {
          return true;
        }

        if (this.isRowActionTriggerByDoubleClick()) {
          // emit row action for this table
          this.emit(context.constants.widgetEvents.rowAction);
        }
        return false;
      },

      /**
       * Open the table configuration side panel bound to this table
       */
      openConfigSidePanel: function () {
        if (this._configSidePanel) {
          return;
        }

        const opts = this.getBuildParameters();
        opts.inTable = false; // the panel lives outside the table, at viewport level
        opts.ignoreLayout = true;
        // same capability source as the sort apply path (TableSortUIBehavior)
        opts.isMultiColumnSortSupported = this.isMultiColumnSortSupported();

        this._configSidePanel = cls.WidgetFactory.createWidget("TableConfigSidePanel", opts);
        this._configSidePanel.setTableWidget(this);
        document.body.appendChild(this._configSidePanel.getElement());
        this._configSidePanel.show();
        this._setConfiguring(true);

        this._configSidePanel.onClose(
          function () {
            const panel = this._configSidePanel;
            this._configSidePanel = null;
            this._setConfiguring(false);
            if (panel && !panel.isDestroyed()) {
              panel.destroy();
            }
          }.bind(this),
          true
        );
      },

      /**
       * Toggle the editing decoration shown on the table while its configuration panel is open
       * @param {boolean} configuring - true when the panel is open
       * @private
       */
      _setConfiguring: function (configuring) {
        this.toggleClass("gbc_TableConfiguring", configuring);
        if (configuring && !this._editingTagElement) {
          this._editingTagElement = document.createElement("div");
          this._editingTagElement.className = "gbc_RTableWidget__editingTag";
          this._editingTagElement.setAttribute("aria-live", "polite");
          this.getElement().appendChild(this._editingTagElement);
          // set the text once the live region is in the DOM so screen readers announce it
          this.afterDomMutator(
            function () {
              if (this._editingTagElement) {
                this._editingTagElement.textContent = gbc.I18NService.t("gwc.tableConfig.editing");
              }
            }.bind(this)
          );
        } else if (!configuring && this._editingTagElement) {
          this._editingTagElement.remove();
          this._editingTagElement = null;
        }
      },

      /**
       * Fill a context menu with the usual table context menu elements
       * @param contextMenu - context menu to fill
       * @param opts - application build parameters
       */
      fillContextMenu: function (contextMenu, opts) {
        const columns = this.getColumns();
        const hasSizableColumns = columns.some((c) => c.isSizable());
        const hasSortableColumns = columns.some((c) => c.isSortable());
        const hasHideableColumns = columns.some((c) => c.isHidable());
        const hasMovableColumns = columns.some((c) => c.isMovable());

        // Show those entries only if column is hidable
        if (hasHideableColumns) {
          // Be sure that the last checkbox is always check (cannot hide all columns)
          let checkLast = function () {
            const children = contextMenu.getChildren();
            let checkCount = children.filter(function (c) {
              const colWidget = c._colWidget;
              if (colWidget) {
                // Re-enables in case it was previously last but no longer, if it is checkable
                c.setEnabled(colWidget.isCheckable());
                if (c.isInstanceOf(cls.CheckBoxWidget) && c.getValue()) {
                  return c;
                }
              }
            });
            if (checkCount.length === 1) {
              checkCount[0].setEnabled(false);
            }
          };

          // Hide/show columns
          let hideShowFunc = function () {
            this.emit(gbc.constants.widgetEvents.tableShowHideCol, "toggle");

            // refocus UI widget to keep key processing & dropdown manage keys active
            // because dropdown element is outside UserInterface in the HTML Dom
            let tableWidget = this.getTableWidgetBase();
            if (tableWidget._contextMenu && tableWidget._contextMenu.isVisible()) {
              let uiWidget = tableWidget.getUserInterfaceWidget();
              if (uiWidget) {
                this.domFocus();
              }
            }
          };

          let checkNextValue = function () {
            this.setValue(this.getNextValue());
          };

          // for each col add hide/show checkbox
          for (const col of this.getOrderedColumns()) {
            if (!col.isAlwaysHidden()) {
              let check = cls.WidgetFactory.createWidget("CheckBox", opts);
              check._colWidget = col;
              check.setEnabled(col.isCheckable());
              check.setText(col.getText());
              check.setValue(col.isVisible());
              check.when(context.constants.widgetEvents.valueChanged, checkNextValue.bind(check));
              check.when(context.constants.widgetEvents.click, checkLast.bind(col));
              contextMenu.addChildWidget(check, {
                clickCallback: hideShowFunc.bind(col, check)
              });
            }
          }
          checkLast();
          contextMenu.addSeparator();

          // Show all columns action
          /** @type {classes.MenuLabelWidget} */
          let showAllColumnsLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          showAllColumnsLabel.setValue(gbc.I18NService.t("gwc.contextMenu.showAllColumns"));
          showAllColumnsLabel.addClasses("gbc_showAllColumns_action");
          contextMenu.addChildWidget(showAllColumnsLabel, {
            clickCallback: function () {
              let columns = this.getColumns();
              for (const element of columns) {
                let tc = element;
                if (!tc.isAlwaysHidden() && tc.isHidable()) {
                  tc.emit(gbc.constants.widgetEvents.tableShowHideCol, "show");
                }
              }
            }.bind(this)
          });

          // Hide all columns action
          // Not available in inputmode to avoid cancelling modifications
          if (this.isDisplayMode()) {
            /** @type {classes.MenuLabelWidget} */
            let hideAllColumnsLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
            hideAllColumnsLabel.setValue(gbc.I18NService.t("gwc.contextMenu.hideAllColumns"));
            hideAllColumnsLabel.addClasses("gbc_hideAllColumns_action");
            contextMenu.addChildWidget(hideAllColumnsLabel, {
              clickCallback: function () {
                let columns = this.getOrderedColumns();
                for (const col of columns) {
                  if (col.isVisible()) {
                    col.hideOtherColumns();
                    return;
                  }
                }
              }.bind(this)
            });
          }
        }

        // AutoFit / FitToView only make sense if at least one column is sizable
        if (hasSizableColumns) {
          // AutoFit column width based on values
          /** @type {classes.MenuLabelWidget} */
          let autofitAllColumnsLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          autofitAllColumnsLabel.setValue(gbc.I18NService.t("gwc.contextMenu.autofitAllColumns"));
          autofitAllColumnsLabel.addClasses("gbc_autofitAllColumns_action");
          contextMenu.addChildWidget(autofitAllColumnsLabel, {
            clickCallback: function () {
              this.autofitAllColumns();
            }.bind(this)
          });

          // Fit column width so all columns visible
          // Example: if table width = 600px and there are two columns currently 100 and 200px
          // then this will set width of columns to 200 and 400 px respectively
          /** @type {classes.MenuLabelWidget} */
          let fitToViewAllColumnsLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          fitToViewAllColumnsLabel.setValue(gbc.I18NService.t("gwc.contextMenu.fitToViewAllColumns"));
          fitToViewAllColumnsLabel.addClasses("gbc_fitToViewAllColumns_action");
          contextMenu.addChildWidget(fitToViewAllColumnsLabel, {
            clickCallback: function () {
              this.fitToViewAllColumns();
            }.bind(this)
          });
        }

        // "Restore default settings" resets order, width, visibility and sort — only useful
        // if at least one of these customizations is allowed.
        const hasRestorableSettings = hasHideableColumns || hasSizableColumns || hasSortableColumns || hasMovableColumns;

        if (hasRestorableSettings) {
          contextMenu.addSeparator();

          // Reset to default action
          /** @type {classes.MenuLabelWidget} */
          let resetDefaultLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          resetDefaultLabel.setValue(gbc.I18NService.t("gwc.contextMenu.restoreDefaultSettings"));
          resetDefaultLabel.addClasses("gbc_restoreDefaultSettings_action");
          contextMenu.addChildWidget(resetDefaultLabel, {
            clickCallback: function () {
              this.emit(context.constants.widgetEvents.tableResetToDefault);
            }.bind(this)
          });
        }

        // Reset sort order action only if at least one column is sortable
        if (hasSortableColumns) {
          /** @type {classes.MenuLabelWidget} */
          let resetLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          resetLabel.setValue(gbc.I18NService.t("gwc.contextMenu.restoreColumnSort"));
          resetLabel.addClasses("gbc_restoreColumnSort_action");
          contextMenu.addChildWidget(resetLabel, {
            clickCallback: function () {
              this.emit(context.constants.widgetEvents.tableHeaderSort, -1);
            }.bind(this)
          });
        }

        // Open the table configuration side panel — only relevant if something is configurable
        if (hasRestorableSettings) {
          contextMenu.addSeparator();

          /** @type {classes.MenuLabelWidget} */
          let configureTableLabel = cls.WidgetFactory.createWidget("MenuLabel", opts);
          configureTableLabel.setValue(gbc.I18NService.t("gwc.contextMenu.configureTable"));
          configureTableLabel.addClasses("gbc_configureTable_action");
          contextMenu.addChildWidget(configureTableLabel, {
            clickCallback: function () {
              this.openConfigSidePanel();
            }.bind(this)
          });
        }
      },

      /**
       * Build context menu and show it
       */
      _buildContextMenu: function (domEvent) {
        if (this._contextMenu) {
          this._contextMenu.destroyChildren();
          this._contextMenu.destroy();
          this._contextMenu = null;
        }

        let opts = this.getBuildParameters();
        opts.inTable = false; // contextmenu is not really in the table, it is outside
        opts.ignoreLayout = true;

        this._contextMenu = cls.WidgetFactory.createWidget("ContextMenu", opts);
        this._contextMenu.allowMultipleChoices(true);
        this._contextMenu.setParentWidget(this);
        this._contextMenu.setColor(this.getColor());
        this._contextMenu.setBackgroundColor(this.getBackgroundColor());
        // Capture the local reference: closures use `currentMenu` (not `this._contextMenu`) so that
        // an in-flight onClose deferred via afterDomMutator never operates on a different menu
        // instance built by a rapid second click.
        const currentMenu = this._contextMenu;
        // Sync the table menu button aria state with the context menu lifecycle (uses onOpen
        // rather than writing eagerly because the menu may be discarded if it ends up empty —
        // see the early return below).
        currentMenu.onOpen(
          function () {
            // Symmetric guard with onClose below — `_menuButtonComponent` may be gone by the time
            // this fires if the table got destroyed between menu open trigger and emit.
            if (!this._menuButtonComponent) {
              return;
            }
            const btn = this._menuButtonComponent.getElement();
            cls.DOMElementHelper.updateAttribute(btn, "aria-controls", currentMenu.getRootClassName());
            cls.DOMElementHelper.updateAttribute(btn, "aria-expanded", "true");
          }.bind(this)
        );
        currentMenu.onClose(
          function () {
            // Hide the menu button if it's visible
            this.afterDomMutator(
              function () {
                // Only act if `currentMenu` is still the active menu — guards against a rapid
                // re-open that swapped `this._contextMenu` while this onClose was pending.
                if (this._contextMenu === currentMenu) {
                  this._contextMenu.destroyChildren();
                  this._contextMenu.destroy();
                  this._contextMenu = null;

                  if (this._menuButtonComponent) {
                    this._menuButtonComponent.unfreezeAndHide();
                    const btn = this._menuButtonComponent.getElement();
                    cls.DOMElementHelper.updateAttribute(btn, "aria-expanded", "false");
                    cls.DOMElementHelper.updateAttribute(btn, "aria-controls", null);
                  }
                }
              }.bind(this)
            );
          }.bind(this),
          true
        );

        this.fillContextMenu(this._contextMenu, opts);

        // If nothing could be offered (e.g. all of UNHIDABLECOLUMNS, UNSIZABLECOLUMNS,
        // UNSORTABLECOLUMNS, UNMOVABLECOLUMNS are set), don't open an empty menu.
        if (this._contextMenu.getChildren().length === 0) {
          this._contextMenu.destroy();
          this._contextMenu = null;
          this._menuButtonComponent.unfreezeAndHide();
          return;
        }

        // beware setFocus should not raise a scroll event (it will immediately close contextmenu)
        this.domFocus(true);

        this._contextMenu.reverseX = true;
        this._contextMenu.parentElement = domEvent.target;
        this._menuButtonComponent.freeze();
        this._contextMenu.show();
      },

      /**
       * @inheritDoc
       */
      buildExtraContextMenuActions: function (contextMenu, sourceWidget) {
        // Clipboard actions are only added if clipboard API is available
        if (!context.ClipboardService.isApiAvailable()) {
          if (this.getParentWidget()) {
            this.getParentWidget().buildExtraContextMenuActions(contextMenu, sourceWidget);
          }
          return;
        }

        if (this._selectionSquareIdx.left.x !== null) {
          contextMenu.addAction(
            "copy",
            gbc.I18NService.t("gwc.clipboard.copy"),
            null,
            "Control+C",
            {
              clickCallback: function (contextMenu) {
                contextMenu.hide();
                this._copySelectionInClipboard();
              }.bind(this)
            },
            true
          );
        }

        // A current line must be defined and all the selected items must be on the same row (if a selection is defined)
        if (this.canCopyCellOrRow()) {
          this.emit(gbc.constants.widgetEvents.buildContextMenuCopyArrayActions, contextMenu, sourceWidget);
        }

        if (this.isMultiRowSelectionEnabled()) {
          contextMenu.addAction(
            "selectAll",
            gbc.I18NService.t("gwc.contextMenu.selectAll"),
            "font:FontAwesome.ttf:f0ea",
            "Ctrl+A",
            {
              clickCallback: function () {
                contextMenu.hide();
                this.emit(context.constants.widgetEvents.selectAll);
              }.bind(this)
            },
            true
          );
        }

        if (this.getParentWidget()) {
          this.getParentWidget().buildExtraContextMenuActions(contextMenu, sourceWidget);
        }
      },

      /**
       * Invalidate measure to force width & row height measure in next layout cycle
       */
      resetMeasure: function () {
        this.resetLayout();
        this.getLayoutEngine().forceMeasurement();
        this.getLayoutEngine().invalidateMeasure();
        if (this.getLayoutEngine().invalidatePreferredSize) {
          this.getLayoutEngine().invalidatePreferredSize();
        }
      },

      /**
       * Update table depending on parameters
       * @param {boolean} resetOrderedColumns - reset ordered columns
       * @param {boolean} layoutRow - update row height layout
       * @param {boolean} layoutCol - update col width layout
       */
      update: function (resetOrderedColumns, layoutRow, layoutCol) {
        if (resetOrderedColumns) {
          this.resetOrderedColumns();
        }
        if (this.getLayoutEngine().computeRowsColsCss(layoutRow, layoutCol)) {
          this.getLayoutEngine().measureDecoration();
          this.updateAllAggregate();
          this.updateFrozenColumns();
        }
      },

      /**
       * Reset the measure and recompute the table layout
       * Usefull when flipping the table or changing its presentation
       */
      updateLayout: function () {
        this.resetMeasure();
        this._initLayout();
        this.update(false, true, true);
        this.getLayoutEngine().computeRowsColsCss(true, true, true);
        this.emit(context.constants.widgetEvents.layout);
        this.updateVerticalScroll(true);
      },

      /**
       * Change the type of view (classic, flipped)
       * @param {String} viewType - type of view (classic, flipped)
       * @param {boolean} [forceLayout] - if true, force layout of table
       */
      setViewType: function (viewType, forceLayout = false) {
        if (this.getViewType() === viewType) {
          return;
        }

        this._viewType = viewType;
        this.getElement().setAttribute("viewType", viewType);

        // Reset the layout engine
        if (this._layoutEngine && forceLayout) {
          this.updateLayout();
        }
      },

      /**
       * Return type of view (classic, flipped)
       * @return {String} (classic, flipped)
       */
      getViewType: function () {
        return this._viewType;
      },

      /**
       * Is flipped view ?
       * @return {boolean} is flipped view ?
       */
      isFlipped: function () {
        return this.getViewType() === "flipped";
      },

      /**
       * Enable/disable alternate row color
       * @param {boolean} doAlternateRows - enable/disable alternate row color
       * @param {boolean} forceUpdate - force update of alternate row colors
       * @param {boolean} refresh - reload colors from theme service
       */
      setAlternateRows: function (doAlternateRows, forceUpdate = false, refresh = false) {
        let update = forceUpdate;
        if (this._alternateRows !== doAlternateRows || refresh) {
          this._alternateRows = doAlternateRows;
          let evenRowBackgroundColor = context.ThemeService.getValue("gbc-TableWidget-even-row-background-color");
          let oddRowBackgroundColor = context.ThemeService.getValue("gbc-TableWidget-odd-row-background-color");
          this._evenRowBackgroundColor = this._alternateRows ? evenRowBackgroundColor : "inherit";
          this._oddRowBackgroundColor = this._alternateRows ? oddRowBackgroundColor : "inherit";
          update = true;
        }
        if (update) {
          // update odd and even backcolor
          // if diff offset is odd, switch odd/even colors
          // TODO : It would be nice to find a way not to do a set style everytime we scroll.
          // By numbering the rows for example
          // OR by replacing the rows 2 by 2
          let switchOddEven = this.getOffset() % 2 !== 0;
          this.getElement().style.setProperty("--evenRowBackgroundColor", switchOddEven ? this._oddRowBackgroundColor : this._evenRowBackgroundColor);
          this.getElement().style.setProperty("--oddRowBackgroundColor", switchOddEven ? this._evenRowBackgroundColor : this._oddRowBackgroundColor);
        }
      },

      /**
       * Called when a scroll is done
       * @param {Object} event - scroll event
       */
      _onScroll: function (event) {
        const scrollLeft = event.target.scrollLeft;
        const scrollTop = event.target.scrollTop;
        const leftScrolled = this._previousScrollLeftValue !== scrollLeft;

        if (this._previousScrollTopValue !== scrollTop) {
          this._previousScrollTopValue = scrollTop;
          if (event.target) {
            // Emit scroll event for vertical scrolling
            this.emit(context.constants.widgetEvents.scroll, event, this.getRowHeight());
          }
        }

        if (leftScrolled) {
          this._previousScrollLeftValue = scrollLeft;
          // synchronize header & data horizontal scroll
          this.getHeaderGroupElement().scrollLeft = scrollLeft;
          this.getFooterGroupElement().scrollLeft = scrollLeft;

          // remove hidden columns from DOM during horizontal scroll
          this.updateVisibleColumnsInDom(scrollLeft);
        }

        // reset items selection after each scroll change
        this._resetItemsSelection();
      },

      /**
       * Update the list of columns which are in the DOM according to current viewport and horizontal scroll position (optimisation)
       * @param {number} scrollLeft
       * @param {boolean} forceColsMeasure
       * @private
       */
      updateVisibleColumnsInDom: function (scrollLeft, forceColsMeasure = false) {
        this._resetItemsSelection();
        if (!this._layoutInformation) {
          return;
        }
        const isVisible = this.isVisibleRecursively();
        if (!isVisible) {
          return;
        }

        // read scrollLeft if specified otherwise retrieve it
        scrollLeft = typeof scrollLeft !== "undefined" ? scrollLeft : this.getScrollableArea().scrollLeft;

        const columns = this.getOrderedColumns();
        const tableWidth = this._layoutInformation.getAllocated().getWidth();
        let currentWidth = 0;
        const colsToRemove = [];
        const colsToAdd = [];

        for (const col of columns) {
          if (!this.isFlipped() && !col.isFrozen() && col.isVisible()) {
            // scrollable columns
            const colLayoutInfo = col.getLayoutInformation();
            if (colLayoutInfo) {
              let colWidth = col.getWidth();

              // Use stretchmin width instead if it is greater than the measured width
              if (colLayoutInfo.isXStretched()) {
                const rawStretchMin = colLayoutInfo.getRawInformation().getStretchMin();
                if (rawStretchMin !== 0) {
                  const stretchMin = cls.CharSize.translate(
                    rawStretchMin,
                    colLayoutInfo.getCharSize().getWidthM(),
                    colLayoutInfo.getCharSize().getWidth0()
                  );
                  colWidth = stretchMin > colWidth ? stretchMin : colWidth;
                }
              }

              // Detach columns which are not visible at left
              if (currentWidth + colWidth < scrollLeft || currentWidth - scrollLeft > tableWidth - this._totalLeftFrozenColumnWidth) {
                colsToRemove.push(col);
              } else {
                colsToAdd.push(col);
              }
              currentWidth += colWidth;
            }
          } else if (!col.isVisible()) {
            colsToRemove.push(col);
          } else {
            colsToAdd.push(col);
          }
        }

        let measureCols = false;
        if (colsToRemove.length > 0 || colsToAdd.length > 0) {
          const prepend = scrollLeft < this._previousScrollLeftValue;
          /**
           * when prepending columns, reverse orders to prepend them from the last one to the first one
           * we try to conserve correct dom orders for columns and items even if its unnecessary
           * because processed by their CSS order attribute, but it's nice to have
           */
          if (prepend) {
            colsToAdd.reverse();
          }
          colsToRemove.forEach((c) => {
            c.detachItemsFromDom();
            c.reorderAggregateLabel();
          });
          colsToAdd.forEach((c) => {
            c.attachItemsToDom(prepend);
            c.reorderAggregateLabel();
          });
          measureCols = true;
        }

        if (forceColsMeasure || measureCols) {
          // update CSS grid template
          this.getLayoutEngine().computeRowsColsCss(false, true, false);
        }
      },

      /**
       * @inheritDoc
       */
      setScrolling: function (up, down) {
        this._isScrolling = up || down;
        // unnecessary for now
        //cls.DOMElementHelper.toggleClass(this.getScrollerElement(), "scrollingUp", up);
        //cls.DOMElementHelper.toggleClass(this.getScrollerElement(), "scrollingDown", down);
      },

      /**
       * Returns item widget
       * @param {number} row - item row
       * @param {number} column - item col
       * @returns {classes.RTableItemWidget} item widget
       * @publicdoc
       */
      getItem: function (row, column) {
        return this.getChild(row)?.getChild(column);
      },

      /**
       * Returns row widgets
       * @returns {classes.RTableRowWidget[]} array of row widgets
       * @publicdoc
       */
      getRows: function () {
        return this.getChildren();
      },

      /**
       * Returns header row widget
       * @returns {classes.RTableRowWidget} header row widget
       * @publicdoc
       */
      getHeaderRowWidget: function () {
        return this._headerRowWidget;
      },

      /**
       * Returns footer row widget
       * @returns {classes.RTableRowWidget} footer row widget
       * @publicdoc
       */
      getFooterAggregatesRowWidget: function () {
        return this._footerAggregatesRowWidget;
      },

      /**
       * @inheritDoc
       */
      setPageSize: function (pageSize) {
        if (this._pageSize !== pageSize) {
          this.getElement().style.setProperty("--pageSize", `${pageSize}`);
          this._resetItemsSelection(); // reset items selection after pageSize change
        }
        $super.setPageSize.call(this, pageSize);
      },

      /**
       * @inheritDoc
       */
      setBufferSize: function (bufferSize) {
        if (this._bufferSize !== bufferSize) {
          this.getElement().style.setProperty("--bufferSize", `${bufferSize}`);
        }
        $super.setBufferSize.call(this, bufferSize);
      },

      /**
       * @inheritDoc
       */
      setSize: function (size) {
        if (this._size !== size) {
          // in input one extra line for user to click after last line
          size = this.isInputMode() || this.isInputArrayMode() ? size + 1 : size;
          this.getElement().style.setProperty("--size", `${size}`);

          // if size of table changed, update row number in cached data model
          this._cachedDataModel.updateNbRows(size);
        }
        $super.setSize.call(this, size);
      },

      /**
       * @inheritDoc
       */
      addChildWidget: function (widget, options) {
        options = options || {};

        if (options.isRowData) {
          let rowWidget = this.getRows()[options.rowIndex];

          // create row widget
          if (!rowWidget) {
            rowWidget = cls.WidgetFactory.createWidget("RTableRow", this.getBuildParameters());

            // don't hide first row it is used for layout computation
            let hideRow = options.rowIndex >= this.getVisibleRowCount() && options.rowIndex !== 0;
            rowWidget.setHidden(hideRow);

            $super.addChildWidget.call(this, rowWidget);

            rowWidget.onAttachedToParentTable();
          }
          // add item to row widget
          rowWidget.addChildWidget(widget, options);
        } else if (widget.isInstanceOf(cls.RTableColumnWidget)) {
          options.headerItem = true;
          options.colWidget = widget;
          widget.setOrder(this.getHeaderRowWidget().getChildren().length, true); // set order on header columns as well
          this.getHeaderRowWidget().addChildWidget(widget, options);
        } else {
          $super.addChildWidget.call(this, widget, options);
        }

        this.resetOrderedColumns();
      },

      /**
       * @inheritDoc
       */
      removeChildWidget: function (widget) {
        $super.removeChildWidget.call(this, widget);
        this.resetOrderedColumns();
      },

      /**
       * @inheritDoc
       */
      setVisibleRowCount: function (visibleRowCount) {
        if (this.getVisibleRowCount() === visibleRowCount) {
          return;
        }
        $super.setVisibleRowCount.call(this, visibleRowCount);
        this.updateVisibleColumnsInDom();
        let rows = this.getRows();
        for (let i = 0; i < rows.length; ++i) {
          let row = rows[i];
          row.setHidden(i >= visibleRowCount);
        }
      },

      /**
       * @inheritDoc
       */
      setVerticalScroll: function (offset, forceScroll = false) {
        // Pagination nav WIP
        //if (this._paginationWidget) {
        //  this._paginationWidget.update(size, pageSize, offset);
        //}

        if (!this.getLayoutEngine().isLayoutDone()) {
          return;
        }

        let top = 0;
        if (this.isEnabled()) {
          top = offset * this.getRowHeight();
        }
        this.getElement().style.setProperty("--scrollTop", `${top}px`);
        // if offset changed or if scrolltop value of current scrollarea is too different from calculated value
        if (Boolean(forceScroll) || ((this.lastSentOffset === null || this.lastSentOffset !== offset) && offset !== this.getOffset())) {
          this.setOffset(offset);
          this.doScroll(top, false);
        }
        this.lastSentOffset = null;
      },

      /**
       * Returns if vertical scroll bar is at end
       * @returns {boolean} true if vertical Scroll bar is at end
       */
      isVerticalScrollAtEnd: function () {
        let scrollArea = this.getScrollableArea();
        return scrollArea.scrollTop + scrollArea.clientHeight === scrollArea.scrollHeight;
      },

      /**
       * Do native vertical scroll
       * @param {number} value - new scroll value
       * @param {boolean} delta - if true, value is added to old scroll value
       * Is the parameter "delta" really relevant. Do we have client customisation that override this method
       */
      doScroll: function (value, delta) {
        if (delta) {
          value = this.getScrollableArea().scrollTop + value;
        }
        // update this in case of element is not in DOM, event scroll will not be emitted
        this._previousScrollTopValue = value;

        this.getScrollableArea().scrollTo({
          top: value,
          behavior: "instant"
        });
      },

      /**
       * Do a horizontal scrolling (column by column)
       * @param {string} direction - "left" or "right"
       */
      doHorizontalScroll: function (direction) {
        let scrollArea = this.getScrollableArea();
        let scrollPos = scrollArea.scrollLeft;
        let columns = this.getOrderedColumns();
        let width = 0;
        for (const element of columns) {
          let col = element;
          if (col.isFrozen() || !col.isVisible()) {
            continue;
          }
          let colWidth = col.getWidth();

          let isScrollAtStartColumn = Math.abs(scrollPos - width) <= 2;
          let isScrollAtEndColumn = Math.abs(scrollPos - (width + colWidth)) <= 2;
          if ((isScrollAtStartColumn || scrollPos > width) && (isScrollAtEndColumn || scrollPos < width + colWidth)) {
            if (isScrollAtStartColumn && direction === "right") {
              scrollPos = width + colWidth;
              direction = "left";
            } else if (isScrollAtEndColumn && direction === "right") {
              scrollPos = width + colWidth;
            } else {
              scrollArea.scrollLeft = direction === "right" ? width + colWidth : width;
              break;
            }
          }
          width += colWidth;
        }
      },

      /**
       * @inheritDoc
       */
      setCurrentRow: function (row, ensureRowVisible = false, vmCurrentRow = null) {
        if (vmCurrentRow !== null) {
          this._vmCurrentRow = vmCurrentRow;
        }
        this._currentRow = row;

        const children = this.getRows();
        const length = children.length;
        let isCurrent = false;
        for (let i = 0; i < length; ++i) {
          let rowWidget = children[i];
          isCurrent = i === row;
          rowWidget.setCurrent(isCurrent);
          rowWidget.setHighlight(this._highlightComponent.getShouldHighlightCurrentRow() && isCurrent && !this.isMultiRowSelectionEnabled());
        }
      },

      /**
       * @inheritDoc
       */
      setCurrentColumn: function (col) {
        this._currentColumn = col;
        let columns = this.getColumns();
        for (let i = 0; i < columns.length; i++) {
          if (columns[i].setCurrent) {
            columns[i].setCurrent(i === col);
          }
        }
      },

      /**
       * Update current item
       */
      updateCurrentItem: function () {
        const currentColumn = this.getColumns()[this._currentColumn];
        const currentItem = currentColumn?.getChild(this._currentRow);
        if (currentItem === this._currentItem) {
          return;
        }
        this._currentItem?.setCurrent(false);
        this._currentItem?.setHighlight(false);
        this._currentItem = currentItem;
        if (!this._currentItem) {
          return;
        }
        this._currentItem?.setCurrent(true);
        this._currentItem?.setHighlight(this._highlightComponent.getShouldHighlightCurrentCell() && !this.isMultiRowSelectionEnabled());
      },

      /**
       * Generates a manual horizontal scroll (if needed) to make sure focused column item if visible and thus attached to DOM
       */
      scrollToCurrentColumn: function () {
        // need to scroll the table horizontally
        if (this.isFlipped()) {
          return;
        }
        const columns = this.getOrderedColumns();
        const currentCol = columns[this._currentColumn];
        if (currentCol && currentCol.isDetachedFromDom()) {
          let minScrollLeft = 0;
          // calculate required scrollLeft to make the focused column visible
          for (const col of columns) {
            if (col.isFrozen() || !col.isVisible()) {
              continue;
            }
            if (col.isCurrent()) {
              break;
            }
            minScrollLeft += col.getWidth();
          }

          // substract table width (viewport width) from the calculated scrollLeft to horizontally center the focused column toward the visible viewport zone
          this.getScrollableArea().scrollLeft = Math.max(minScrollLeft - this._layoutInformation.getAllocated().getWidth() / 2, 0);
        }
      },

      /**
       * Returns current column index
       * @returns {number} current column index
       * @public
       */
      getCurrentColumn: function () {
        return this._currentColumn;
      },

      /**
       * Add a table column (virtual)
       * @param {classes.RTableColumnWidget} col - table column
       */
      addColumn: function (col) {
        this._columns.push(col);
      },

      /**
       * Remove a table column (virtual)
       * @param {classes.RTableColumnWidget} col - table column
       */
      removeColumn: function (col) {
        if (!col || !this._columns || this._columns.length === 0) {
          return;
        }
        // Remove reference to current item if it was in that column
        if (col.getChildren().some((child) => this._currentItem === child)) {
          this._currentItem = null;
        }
        this._columns = this._columns.filter((column) => column !== col);
        if (!this._orderedColumns) {
          return;
        }
        this._orderedColumns = this._orderedColumns.filter((column) => column !== col);
      },

      /**
       * Returns an array with all table columns
       * @return {classes.RTableColumnWidget[]} table columns array
       */
      getColumns: function () {
        return this._columns;
      },

      /**
       * Reset cache of ordered columns.
       */
      resetOrderedColumns: function () {
        this._orderedColumns = null;
      },

      /**
       * Returns column widgets (visual order)
       * @returns {classes.RTableColumnWidget[]} array of column widgets
       * @publicdoc
       */
      getOrderedColumns: function () {
        let columns = this.getColumns();
        if (this._orderedColumns === null || columns.length !== this._orderedColumns.length) {
          let children = columns.slice();
          children.sort(function (a, b) {
            return a.getOrder() - b.getOrder();
          });
          this._orderedColumns = children;
        }
        return this._orderedColumns;
      },

      /**
       * @inheritDoc
       */
      setSimpleSort: function (sortOrder, sortColumn) {
        const columns = this.getColumns();

        // keep the same normalized shape as the multi-column path for getSortSpec consumers
        if (sortColumn > -1 && (sortOrder === "asc" || sortOrder === "desc")) {
          this._sortSpec = [{ colName: "", colIndex: sortColumn, order: sortOrder === "asc" ? "+" : "-", kind: "U", position: 1 }];
        } else {
          this._sortSpec = [];
        }

        for (let i = 0; i < columns.length; i++) {
          if (i === sortColumn) {
            columns[i].setSortDecorator(sortOrder);
          } else {
            columns[i].setSortDecorator("");
          }
        }

        // Notify sort state consumers (e.g. the config side panel refreshes its Sort rules)
        this.emit(context.constants.widgetEvents.tableColumnsChanged);
      },

      /**
       * @inheritDoc
       */
      setMultiColumnSort: function (sortArray) {
        const columns = this.getColumns();
        this._sortSpec = sortArray;

        // Erase all sort decorators
        for (const col of columns) {
          col.setSortDecorator("");
        }

        // Apply new sort decorators
        for (const sort of sortArray) {
          if (sort.colIndex >= 0) {
            columns[sort.colIndex].setSortDecorator(sort.order, sort.position, sort.kind);
          }
        }

        // Add a class to know if there are only one column sorted or multi-column sorted
        this.toggleClasses(sortArray.length > 1, "gbc_MultiColumnSort");

        // Notify sort state consumers (e.g. the config side panel refreshes its Sort rules)
        this.emit(context.constants.widgetEvents.tableColumnsChanged);
      },

      /**
       * Returns the current sort rules in priority order (multi-column sort only)
       * @returns {Array<{colName: string, colIndex: number, order: string, kind: string, position: number}>}
       */
      getSortSpec: function () {
        return this._sortSpec || [];
      },

      /**
       * Enable/disable row hover
       * @param {boolean} rowHover - if true enable row hover, else disable it
       */
      setRowHover: function (rowHover) {
        if (this._rowHover !== rowHover) {
          this._rowHover = rowHover;
          this.toggleClass("rowHover", Boolean(rowHover));
        }
      },

      /**
       * @param {boolean} enable - true if the table should allow multi-row selection, false otherwise
       */
      setMultiRowSelectionEnabled: function (enable) {
        if (this._multiRowSelectionEnabled !== enable) {
          this._multiRowSelectionEnabled = enable;
          this.toggleClass("gbc_multiRowSelectionEnabled", enable);
        }
      },

      /**
       * Returns if multi-row selection is enabled
       * @returns {boolean} true if the table allow multi-row selection, false otherwise
       * @publicdoc
       */
      isMultiRowSelectionEnabled: function () {
        return this._multiRowSelectionEnabled;
      },

      /**
       * Sets the specified row is selected
       * @param {number} rowIndex - index of the row
       * @param {boolean} isSelected - true if the row should be selected, false otherwise
       */
      setRowSelected: function (rowIndex, isSelected) {
        const row = this.getRows()[rowIndex];
        if (!row) {
          return;
        }
        const selectRow = isSelected && !this.isInputMode();
        row.setSelected(selectRow);
      },

      /**
       * Returns if the specified row is selected
       * @param {number} rowIndex - index of the row
       * @returns {boolean} true if the row is selected, false otherwise
       */
      isRowSelected: function (rowIndex) {
        return rowIndex < this.getRows().length && this.getRows()[rowIndex].isSelected();
      },

      /**
       * Auto-fit all column widths
       */
      autofitAllColumns: function () {
        if (!this.isDestroyed()) {
          let columns = this.getColumns();
          for (const tc of columns) {
            tc.autoSetWidth(true);
          }

          // relayout & update css properties once all columns have been re-measured
          this.update(false, false, true);
          this.updateVisibleColumnsInDom();
        }
      },

      /**
       * Auto fit column widths so all columns visible.
       */
      fitToViewAllColumns: function () {
        // phase 1 work out total
        let visibleColumns = this.getOrderedColumns().filter((column) => column.isVisible());
        let availableSpace = this.getDataAreaWidth();
        let sizableColumnsTotalWidth = 0;
        for (const column of visibleColumns) {
          if (column.isSizable()) {
            sizableColumnsTotalWidth = sizableColumnsTotalWidth + column.getWidth();
          } else {
            availableSpace = availableSpace - column.getWidth();
          }
        }

        // Substract the rowbound width from the availableSpace
        if (this.getLayoutInformation().getRowBoundWidth) {
          availableSpace = availableSpace - this.getLayoutInformation().getRowBoundWidth();
        }

        // It means that there is no column to resize
        if (sizableColumnsTotalWidth <= 0) {
          return;
        }

        // phase 2 set column width in proportion to space available
        let sizableColumns = visibleColumns.filter((column) => column.isSizable());
        const transformationRatio = availableSpace / sizableColumnsTotalWidth;
        let availableSpaceLeft = availableSpace;
        let i = 0;
        let width = 0;
        for (const sizableColumn of sizableColumns) {
          // Give the remaining width to the last column
          if (i === sizableColumns.length - 1) {
            width = availableSpaceLeft;
          } else {
            // Round down to avoid getting out of space for the last column
            width = Math.floor(sizableColumn.getWidth() * transformationRatio);
          }
          sizableColumn.setUserWidthFromInteraction(width, true);
          availableSpaceLeft -= width;
          i += 1;
        }

        // relayout & update css properties once all columns have been re-measured
        this.update(false, false, true);
        this.updateVisibleColumnsInDom();
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled) {
        if (this._enabled !== Boolean(enabled)) {
          $super.setEnabled.call(this, enabled);

          this._resetItemsSelection();
          this.updateVerticalScroll(enabled);
        }
      },

      /**
       * Fans out to the table decorations whose Tab-order predicate depends on
       * the table state: column headers (sort), tree anchors (expand/collapse)
       * and rowbound decorators (row actions). Triggered through the root sync
       * on `setEnabled` / `setDialogType`.
       * @inheritDoc
       */
      _updateDOMTabIndexFromState: function () {
        $super._updateDOMTabIndexFromState.call(this);
        for (const column of this.getColumns()) {
          column._updateDOMTabIndexFromState();
          if (column.isTreeView()) {
            // tree anchors only live in the tree column: skip the other cells
            for (const item of column.getChildren()) {
              item._updateDOMTabIndexFromState();
            }
          }
        }
        for (const row of this.getRows()) {
          row.getRowBoundDecorator()?._updateDOMTabIndexFromState();
        }
      },

      /**
       * @inheritDoc
       */
      setBackgroundColor: function (color) {
        $super.setBackgroundColor.call(this, color);
        this.setStyle(".gbc_TableHeaderGroup", {
          "background-color": color && !this._ignoreBackgroundColor ? color : null
        });
      },

      /**
       * @inheritDoc
       */
      setHaveRowBoundActions: function (haveActions) {
        $super.setHaveRowBoundActions.call(this, haveActions);

        // both directions: losing the actions must also drop the decorators
        // from the Tab order (update() re-syncs icon + tabindex + inert)
        this._headerRowWidget.updateRowBound();
        for (const row of this.getRows()) {
          row.updateRowBound();
        }
      },

      /**
       * @inheritDoc
       */
      setDialogType: function (dialogType) {
        const wasDisplayMode = this.isDisplayMode();

        // Disable vertical scrollbar in INPUT mode
        $super.setDialogType.call(this, dialogType);
        // disable vertical scrolling in INPUT mode
        cls.DOMElementHelper.toggleClass(this.getContainerElement(), "overflow-y-hidden", this.isInputMode());
        /** @hack : As a table create labels instead of edits in display array,
         * we need to recompute the layout when changing the dialog type for flipped table,
         * because a small change in pixel size, can mess up the whole row
         * */
        if (this.isFlipped() && this.isDisplayMode() !== wasDisplayMode) {
          this.resetMeasure();
        }
      },

      // ============== START - ANTICIPATE SCROLLING FUNCTIONS ===================
      /**
       * @inheritDoc
       */
      anticipateScrolling: function () {
        // update table widgets from data model
        this.applyDataFromModel(true);

        // Update --scrollTop css variable to set table items at the right position
        const top = this.getOffset() * this.getRowHeight();
        this.getElement().style.setProperty("--scrollTop", `${top}px`);

        // update currentRow
        if (this._vmCurrentRow !== null) {
          this.setCurrentRow(this._vmCurrentRow - this.getOffset(), false);
          this.updateCurrentItem();
        }

        // update alternate rows
        this.setAlternateRows(this._alternateRows, true);
      },

      /**
       * Apply data from model to widget
       * @param {boolean} blur - if true blur rows which have no data in the model
       */
      applyDataFromModel: function (blur) {
        if (!this.isEnabled()) {
          return;
        }
        context.styler.bufferize();
        const tableColumns = this.getColumns();

        let isFirstVisibleColumn = false;
        for (let columnIndex = 0; columnIndex < tableColumns.length; ++columnIndex) {
          const column = tableColumns[columnIndex];
          const columnItems = column.getChildren();

          const maxLoop = Math.min(columnItems.length, this.getBufferSize());
          const isColumnVisible = maxLoop > 0; // if there is now rows --> col is not visible
          isFirstVisibleColumn = isColumnVisible && !isFirstVisibleColumn;

          for (let rowIndex = 0; rowIndex < maxLoop; rowIndex++) {
            const columnItem = columnItems[rowIndex];

            const rowModelIndex = this.getOffset() + rowIndex;
            if (rowModelIndex >= this._cachedDataModel.getData().length) {
              continue;
            }
            const rowModel = this._cachedDataModel.getData()[rowModelIndex];

            if (isFirstVisibleColumn && rowModel.selected !== null) {
              // do it for the first visible column only because it's about the entire row
              // update selected rows
              this.setRowSelected(rowIndex, rowModel.selected);
            }

            if (columnItem.isTreeItem() && rowModel.treeDepth !== null && rowModel.treeLeaf !== null && rowModel.treeExpanded !== null) {
              // apply tree item attributes
              columnItem.setDepth(rowModel.treeDepth);
              columnItem.setLeaf(rowModel.treeLeaf);
              columnItem.setExpanded(rowModel.treeExpanded);
            }

            const data = rowModel.items[columnIndex];
            if (data) {
              // if an item doesn't have data from model yet, then blur it
              columnItem.blur(blur && !rowModel.vm);

              if (columnIndex === 0) {
                // if the first column is blured, blur the rowbound item
                this.getRows()
                  [rowIndex].getRowBoundDecorator()
                  ?.blur(blur && !rowModel.vm);
              }

              // update table items from data model when we are anticipating, otherwise let the behaviors do their job node by node
              for (const prop in data) {
                this._updateItem(columnItem, prop, data[prop]);
              }
            }
          }
        }

        context.styler.flush();
      },

      /**
       * Update table item with datas
       * @param {classes.RTableItemWidget} tableItemWidget
       * @param {String} dataName - data name
       * @param {*} dataValue - data value
       */
      _updateItem: function (tableItemWidget, dataName, dataValue) {
        // return only if dataValue is not defined, if dataValue is null we must set a null value to the widget
        if (dataValue === undefined) {
          return;
        }

        const cellWidget = tableItemWidget.getWidget();

        switch (dataName) {
          case "value":
            if (cellWidget.setValue && (!cellWidget.isEditing || !cellWidget.isEditing())) {
              if (cellWidget instanceof cls.ImageWidget) {
                // for ImageWidget setSrc with directApply is faster than setValue
                cellWidget.setSrc(dataValue, this._isScrolling);
              } else {
                cellWidget.setValue(dataValue, true);
              }
            }
            break;
          case "image":
            tableItemWidget.setImage(dataValue);
            break;
          case "textColor":
            if (cellWidget.setColor) {
              cellWidget.setColor(dataValue);
            }
            break;
          case "textDecoration":
            if (cellWidget.setTextDecoration) {
              cellWidget.setTextDecoration(dataValue);
            }
            break;
          case "backgroundColor":
            tableItemWidget.setBackgroundColor(dataValue);
            break;
          case "fontWeight":
            if (cellWidget.setFontWeight) {
              cellWidget.setFontWeight(dataValue);
            }
            break;
          default:
        }
      },
      // ============== END - ANTICIPATE SCROLLING FUNCTIONS ===================

      // ============== START - HEADER Event/DnD FUNCTIONS ===================

      /**
       * Handle drag start on header
       * @param {DragEvent} evt - dragstart event
       * @private
       */
      _onHeaderDragStart: function (evt) {
        this._columnContainerComponent.onDragStart(evt);
      },

      /**
       * Handle drag end on header
       * @param {DragEvent} evt - dragend event
       * @private
       */
      _onHeaderDragEnd: function (evt) {
        this._columnContainerComponent.onDragEnd(evt);
      },

      /**
       * Handle drag start on header
       * @param {DragEvent} evt - drag event
       * @private
       */
      _onHeaderDrag: function (evt) {
        // Not handled, everything is handled into the _onHeaderDragOver() method
        return;
      },

      /**
       * Handle drag over on header
       * @param {DragEvent} evt - dragover event
       * @private
       */
      _onHeaderDragOver: function (evt) {
        this._columnContainerComponent.onDragOver(evt);
      },

      /**
       * Handle drag leave on header
       * @param {DragEvent} evt - dragleave event
       * @private
       */
      _onHeaderDragLeave: function (evt) {
        this._columnContainerComponent.onDragLeave(evt);
      },

      /**
       * Handle drop event on header
       * @param {DragEvent} evt - drop event
       * @private
       */
      _onHeaderDrop: function (evt) {
        this._columnContainerComponent.onDrop(evt);
      },

      // ====================================
      // TOUCH EVENTS
      // ====================================

      /**
       * Handle Touch Start event
       * @param {TouchEvent} evt
       */
      _onHeaderTouchStart: function (evt) {
        this._columnContainerComponent.onTouchStart(evt);
      },

      /**
       * Handle touch end event
       * @param {Object} evt
       */
      _onHeaderTouchEnd: function (evt) {
        this._columnContainerComponent.onTouchEnd(evt);
      },

      /**
       * Handle Touch move event
       * @param {TouchEvent} evt - The touch event
       */
      _onHeaderTouchMove: function (evt) {
        this._columnContainerComponent.onTouchMove(evt);
      },

      // ============== END - HEADER Event/DnD FUNCTIONS ===================

      // ============== START - MENU BUTTON SHOW / HIDE EVENTS ===================
      /**
       * Handle wheel event
       * @param {WheelEvent} evt
       */
      _onWheel: function (evt) {
        if (evt.deltaY < 0) {
          this._menuButtonComponent.show();
        }
      },

      /**
       * Handle Touch Start event
       * @param {TouchEvent} evt
       */
      _onTouchStart: function (evt) {
        this._previousTouchYValue = evt.touches[0].clientY;
        this._cancelMenuButtonForThisTouchPhase = false;
      },

      /**
       * Handle Touch Move event
       * @param {TouchEvent} evt
       */
      _onTouchMove: function (evt) {
        if (evt.touches.length === 0 || this._cancelMenuButtonForThisTouchPhase) {
          return;
        }

        if (evt.touches[0].clientY > this._previousTouchYValue) {
          this._menuButtonComponent.show();
        } else {
          /*
           * For mobile, if the user do not scroll directly upward,
           * cancel the menu button show.
           */
          this._cancelMenuButtonForThisTouchPhase = true;
        }
      },

      // ============== END - MENU BUTTON SHOW / HIDE EVENTS ===================

      // ============== START - ITEMS CLIENT SELECTION FUNCTIONS ===================
      /**
       * Set if item selection is the default behavior (disable dnd in this case)
       * @param {boolean} b
       */
      setDefaultItemSelection: function (b) {
        this._defaultItemSelection = b;
        if (b === true) {
          this.setDndItemEnabled(false);
        }
      },

      /**
       * Check if this mouse event can allow item selection
       * @param {Object} evt - mouse event
       * @returns {boolean}
       */
      _isEventAllowItemSelection: function (evt) {
        return (
          (this.isDisplayMode() && (evt.ctrlKey || evt.metaKey || this._defaultItemSelection)) ||
          (this.isInputArrayMode() && (evt.ctrlKey || evt.metaKey || (this._defaultItemSelection && !this._enabled)))
        );
      },

      /**
       * Returns true if there are some items selected
       * @returns {boolean} true if there are some items selected
       */
      hasItemsSelected: function () {
        if (this._itemSelectionElement === null) {
          return false;
        }
        return !this._itemSelectionElement.classList.contains("hidden");
      },

      /**
       * Reset items selection
       */
      _resetItemsSelection: function () {
        this._selectionSquareIdx = {
          left: {
            x: null,
            y: null
          },
          right: {
            x: null,
            y: null
          }
        };

        if (this._firstItemSelected !== null) {
          this._itemSelectionInProgress = false;
          this._firstItemSelected = null;
          if (this._itemSelectionElement) {
            this._itemSelectionElement.classList.add("hidden");
          }
          this._setItemSelection(false);
        }

        // Reset the static variable which contains the table with an items selection
        cls.RTableWidget.setTableWithItemsSelection(null);
      },

      /**
       * Handle mouseDown event for table items
       * @param {Object} evt - mousedown event
       */
      _onItemMouseDown: function (evt) {
        //if not left button
        if (evt.which !== 1) {
          return;
        }

        this._mouseDownTarget = evt.target;

        this._itemSelectionMouseMovePrevX = evt.screenX;
        this._itemSelectionMouseMovePrevY = evt.screenY;

        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");

        this._resetItemsSelection();

        // Start item selection
        if (itemWidget && this._isEventAllowItemSelection(evt)) {
          // To avoid text selection in input array
          evt.stopPropagation();
          cls.EventHelper.preventCancelableDefault(evt);

          // Create selection rect element
          if (this._itemSelectionElement === null) {
            this._itemSelectionElement = document.createElement("span");
            this._itemSelectionElement.classList.add("gbc_RTableWidget__itemSelectionArea", "hidden");
            this._element.appendChild(this._itemSelectionElement);
          }
          this._itemSelectionInProgress = true;
          this._firstItemSelected = itemWidget;

          // bind mousemove event
          cls.DOMElementHelper.on(this.getElement(), "mousemove.RTableWidget", this._onItemMouseMove.bind(this));

          this._itemSelectionElement.style.pointerEvents = "none";

          // disable dnd
          this._temporaryEnabledDndOnItem(false, this._firstItemSelected);
        }
      },

      /**
       * Stop item selection in progress
       * @param {Object} evt - mouse event
       */
      _stopInProgressItemSelection: function (evt) {
        this._itemSelectionInProgress = false;
        if (this._isEventAllowItemSelection(evt)) {
          // re-enable dnd
          this._temporaryEnabledDndOnItem(this._dndItemEnabled, this._firstItemSelected);
        }
      },

      /**
       * Handle mouseUp event for table items
       * @param {Object} evt - mouseup event
       */
      _onItemMouseUp: function (evt) {
        // unbind mousemove event
        cls.DOMElementHelper.off(this.getElement(), "mousemove.RTableWidget");

        if (this._itemSelectionElement) {
          this._itemSelectionElement.style.pointerEvents = "auto";
        }

        this._itemSelectionMouseMovePrevX = 0;
        this._itemSelectionMouseMovePrevY = 0;
        this._stopInProgressItemSelection(evt);
      },

      /**
       * Handle mouseLeave event for table items
       * @param {Object} evt - mouseleave event
       */
      _onItemMouseLeave: function (evt) {
        this._stopInProgressItemSelection(evt);
      },

      /**
       * Handle mouseMove event for table items
       * @param {Object} evt - mousemove event
       */
      _onItemMouseMove: function (evt) {
        let movementX = this._itemSelectionMouseMovePrevX ? evt.screenX - this._itemSelectionMouseMovePrevX : 0;
        let movementY = this._itemSelectionMouseMovePrevY ? evt.screenY - this._itemSelectionMouseMovePrevY : 0;

        if (Math.abs(movementX) > 1 || Math.abs(movementY) > 1) {
          // execute code only if movement > 1px
          if (this._itemSelectionInProgress && this._isEventAllowItemSelection(evt)) {
            let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
            if (itemWidget) {
              if (this._firstItemSelected !== null) {
                this._setItemSelection(true, this._firstItemSelected, itemWidget);
              }
            }
          }
        }

        this._itemSelectionMouseMovePrevX = evt.screenX;
        this._itemSelectionMouseMovePrevY = evt.screenY;
      },

      /**
       * Copy current items selection in the clipboard
       */
      _copySelectionInClipboard: function () {
        let rows = [];
        let rowIndex;

        let orderedColumns = this.getOrderedColumns();
        for (const col of orderedColumns) {
          rowIndex = 0;
          for (const item of col.getChildren()) {
            if (item.isClientSelected()) {
              let text = item.getChild(0).getValueForClipboard(true) + "\t";
              if (rows.length <= rowIndex) {
                rows.push(text);
              } else {
                rows[rowIndex] += text;
              }
              rowIndex++;
            }
          }
        }
        for (let i = 0; i < rows.length; ++i) {
          rows[i] = rows[i].substring(0, rows[i].length - 1);
        }
        gbc.ClipboardService.copyFromWidget(this, rows.join("\r\n"));

        this._itemSelectionElement?.classList.add("inClipboard");
      },

      /**
       * Select items
       * @param {boolean} doSelect - true/false select or unselect items
       * @param {classes.RTableItemWidget} [startSelectedItem]
       * @param {classes.RTableItemWidget} [endSelectedItem]
       */
      _setItemSelection: function (doSelect, startSelectedItem, endSelectedItem) {
        let realStartRow = -1;
        let realEndRow = -1;
        let realStartCol = -1;
        let realEndCol = -1;

        this._itemSelectionHasChanged = false;

        if (doSelect && startSelectedItem && endSelectedItem) {
          let startCol = startSelectedItem.getColumnWidget().getOrderedColumnIndex();
          let startRow = startSelectedItem.getRowIndex();
          let endCol = !endSelectedItem ? startCol : endSelectedItem.getColumnWidget().getOrderedColumnIndex();
          let endRow = !endSelectedItem ? startRow : endSelectedItem.getRowIndex();

          this._selectionSquareIdx.left.x = realStartRow = startRow < endRow ? startRow : endRow;
          this._selectionSquareIdx.right.x = realEndRow = startRow < endRow ? endRow : startRow;
          this._selectionSquareIdx.left.y = realStartCol = startCol < endCol ? startCol : endCol;
          this._selectionSquareIdx.right.y = realEndCol = startCol < endCol ? endCol : startCol;

          let mostLeftItem = realStartCol === startCol ? startSelectedItem : endSelectedItem;
          let mostRightItem = realStartCol === startCol ? endSelectedItem : startSelectedItem;
          let left = mostLeftItem.getElement().getBoundingClientRect().left;
          let right = mostRightItem.getElement().getBoundingClientRect().right;

          let mostTopItem = realStartRow === startRow ? startSelectedItem : endSelectedItem;
          let mostBottomItem = realStartRow === startRow ? endSelectedItem : startSelectedItem;
          let top = mostTopItem.getElement().getBoundingClientRect().top;
          let bottom = mostBottomItem.getElement().getBoundingClientRect().bottom;
          let tableTop = this.getElement().getBoundingClientRect().top;
          let tableLeft = this.getElement().getBoundingClientRect().left;

          this.setStyle(".gbc_RTableWidget__itemSelectionArea", {
            left: left - tableLeft + "px",
            top: top - tableTop + "px",
            width: right - left + "px",
            height: bottom - top + "px"
          });

          this._itemSelectionElement.classList.remove("hidden");
          this._itemSelectionHasChanged = true;

          this._itemSelectionElement.classList.remove("inClipboard");
        }

        for (let i = 0; i < this.getOrderedColumns().length; i++) {
          let col = this.getOrderedColumns()[i];
          for (let j = 0; j < col.getChildren().length; j++) {
            let item = col.getChild(j);

            let select = doSelect && i >= realStartCol && i <= realEndCol && j >= realStartRow && j <= realEndRow;
            item.setClientSelected(select);
          }
        }

        cls.RTableWidget.setTableWithItemsSelection(this);
      },

      /**
       * Enable or disable Dnd on a item
       * @param {boolean} b - true/false enable/disable Dnd on item
       * @param {classes.RTableItemWidget} item
       */
      _temporaryEnabledDndOnItem: function (b, item) {
        if (item) {
          item.setDndEnabled(b);

          if (b) {
            this.getContainerElement().setAttribute("draggable", "true");
          } else {
            this.getContainerElement().removeAttribute("draggable");
          }
        }
      },
      // ============== END - ITEMS CLIENT SELECTION FUNCTIONS ===================

      // ============== START - ITEMS DnD FUNCTIONS ===================

      /**
       * Is Dnd of items enabled ?
       * @returns {boolean} is item dnd enabled ?
       */
      isDndItemEnabled: function () {
        return this._dndItemEnabled;
      },

      /**
       * Enable Dnd of items
       * @param {boolean} enableDnd
       */
      setDndItemEnabled: function (enableDnd) {
        if (this._dndItemEnabled === enableDnd) {
          return;
        }
        if (enableDnd && this._defaultItemSelection) {
          return; // no dnd if default is item selection
        }

        this._dndItemEnabled = enableDnd;

        let columns = this.getColumns();
        for (const column of columns) {
          column.setDndItemEnabled(enableDnd);
        }

        let containerElement = this.getContainerElement();
        if (enableDnd) {
          cls.DOMElementHelper.on(containerElement, "dragstart.TableWidget", this._onItemDragStart.bind(this));
          cls.DOMElementHelper.on(containerElement, "dragend.TableWidget", this._onItemDragEnd.bind(this));
          cls.DOMElementHelper.on(containerElement, "dragover.TableWidget", this._onItemDragOver.bind(this));
          cls.DOMElementHelper.on(containerElement, "drop.TableWidget", this._onItemDrop.bind(this));
          cls.DOMElementHelper.on(containerElement, "dragleave.TableWidget", this._onItemDragLeave.bind(this));
          cls.DOMElementHelper.on(containerElement, "dragenter.TableWidget", this._onItemDragEnter.bind(this));
        } else {
          cls.DOMElementHelper.off(containerElement, "dragstart.TableWidget");
          cls.DOMElementHelper.off(containerElement, "dragend.TableWidget");
          cls.DOMElementHelper.off(containerElement, "dragover.TableWidget");
          cls.DOMElementHelper.off(containerElement, "drop.TableWidget");
          cls.DOMElementHelper.off(containerElement, "dragleave.TableWidget");
          cls.DOMElementHelper.off(containerElement, "dragenter.TableWidget");
        }
      },

      /**
       * Trigger onDragStart on the RTableItemWidget that was dragged
       * @param {MouseEvent} evt - dragstart event
       */
      _onItemDragStart: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDragStart) {
          itemWidget.onDragStart(evt);
        }
      },
      /**
       * Trigger onDragEnd on the RTableItemWidget that was dragged
       * @param {MouseEvent} evt - dragend event
       */
      _onItemDragEnd: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDragEnd) {
          itemWidget.onDragEnd(evt);
        }
      },
      /**
       * Handle dragEnter event for table items
       * @param {Object} evt - dragenter event
       */
      _onItemDragEnter: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDragEnter) {
          itemWidget.onDragEnter(evt);
        } else if (evt.target.classList.contains("gbc_TableDataGroup") && !evt.target.contains(evt.relatedTarget)) {
          /* CAUTION : With Webkit browsers this event will be triggered
           * even when we "enter" the container from a child of it.
           * Webkit do not manage dragEnter.relatedTarget
           * But, the DndService should filter the event sent to the VM
           */
          this.emit(gbc.constants.widgetEvents.tableDragEnter, evt);
        }
      },
      /**
       * Handle dragOver event for table items
       * @param {Object} evt - dragover event
       */
      _onItemDragOver: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDragOver) {
          itemWidget.onDragOver(evt);
        } else if (evt.target.classList.contains("gbc_TableDataGroup")) {
          // Find the row equivalent to where the evt.offsetX is
          const dragOverRowIndex = cls.MathHelper.clamp(
            Math.floor(evt.offsetY / this.getLayoutInformation().getRowHeight()),
            0,
            this.getBufferSize()
          );
          this.getColumns()[0]?.emit(gbc.constants.widgetEvents.tableDragOver, dragOverRowIndex, evt);
        }
      },
      /**
       * Handle dragLeave event for table items
       * @param {Object} evt - dragleave event
       */
      _onItemDragLeave: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDragLeave) {
          itemWidget.onDragLeave(evt);
        }
        if (!cls.MathHelper.isInBound(this.getContainerElement().getBoundingClientRect(), evt.clientX, evt.clientY)) {
          /**
           * Trigger this event only if we leave the container
           * for another widget than a child or itself.
           */
          this.emit(gbc.constants.widgetEvents.tableDragLeave, evt);
        }
      },
      /**
       * Handle drop event for table items
       * @param {Object} evt - drop event
       */
      _onItemDrop: function (evt) {
        let itemWidget = gbc.WidgetService.getWidgetFromElement(evt.target, "gbc_RTableItemWidget");
        if (itemWidget && itemWidget.onDrop) {
          itemWidget.onDrop(evt);
        } else if (evt.target.classList.contains("gbc_TableDataGroup")) {
          // Find the row equivalent to where the evt.offsetX is
          const dropRowIndex = cls.MathHelper.clamp(Math.floor(evt.offsetY / this.getLayoutInformation().getRowHeight()), 0, this.getBufferSize());
          this.getColumns()[0].emit(gbc.constants.widgetEvents.tableDrop, dropRowIndex, evt);
        }
      },

      // ============== END - ITEMS DnD FUNCTIONS =====================

      // ============== START - FROZEN COLUMNS FUNCTIONS ===================
      /**
       * Update frozen columns.
       */
      updateFrozenColumns: function () {
        let columns = this.getOrderedColumns();
        this._totalLeftFrozenColumnWidth = 0;
        this._totalVisibleLeftFrozenColumns = 0;
        let totalLeftFrozenColumns = Number.parseInt(this._leftFrozenColumns);

        context.styler.bufferize();
        for (let i = 0; i < columns.length; i++) {
          let currentColumn = columns[i];

          if (i < this._leftFrozenColumns) {
            // left frozen column
            currentColumn.setLastLeftFrozen(i + 1 === this._leftFrozenColumns);
            currentColumn.setLeftFrozen(true);
            currentColumn.setFirstRightFrozen(false);
            currentColumn.setRightFrozen(false);
            // left frozen columns get a negative order to force them to be located even before the grid spacer
            currentColumn.setOrder(-(totalLeftFrozenColumns + 1), true);
            totalLeftFrozenColumns--;
            if (currentColumn.isVisible()) {
              this._totalLeftFrozenColumnWidth += currentColumn.getWidth();
              this._totalVisibleLeftFrozenColumns++;
            }
          } else if (columns.length - i <= this._rightFrozenColumns) {
            // right frozen column
            currentColumn.setLastLeftFrozen(false);
            currentColumn.setLeftFrozen(false);
            currentColumn.setFirstRightFrozen(columns.length - i === this._rightFrozenColumns);
            currentColumn.setRightFrozen(true);
          } else {
            // regular column
            currentColumn.setLastLeftFrozen(false);
            currentColumn.setLeftFrozen(false);
            currentColumn.setFirstRightFrozen(false);
            currentColumn.setRightFrozen(false);
            if (currentColumn.getOrder() < 0) {
              // previously left frozen : replace negative order to normal order
              currentColumn.setOrder(currentColumn.getOrderedColumnIndex(), true);
            }
          }
        }

        context.styler.flush();
      },

      /**
       * Sets the number of left frozen columns.
       * @param {number} n - number of left frozen columns
       * @publicdoc
       */
      setLeftFrozenColumns: function (n) {
        if (this._leftFrozenColumns !== n) {
          this._leftFrozenColumns = n;
        }
      },

      /**
       * Sets the number of right frozen columns.
       * @param {number} n - number of right frozen columns
       * @publicdoc
       */
      setRightFrozenColumns: function (n) {
        if (this._rightFrozenColumns !== n) {
          this._rightFrozenColumns = n;
        }
      },

      /**
       * Returns number of left frozen columns
       * @returns {number} number of left frozen columns
       * @publicdoc
       */
      getLeftFrozenColumns: function () {
        return this._leftFrozenColumns;
      },

      /**
       * Returns number of right frozen columns
       * @returns {number} number of right frozen columns
       * @publicdoc
       */
      getRightFrozenColumns: function () {
        return this._rightFrozenColumns;
      },

      /**
       * Returns number of left frozen columns currently visible
       * @returns {number|*}
       */
      getTotalVisibleLeftFrozenColumns: function () {
        return this._totalVisibleLeftFrozenColumns;
      },

      /**
       * Returns true if table can have frozen columns
       * @returns {boolean} true if table can have frozen columns
       * @publicdoc
       */
      isFrozenTable: function () {
        return this._frozenTable;
      },

      /**
       * Sets if table can contain frozen cols.
       * @param {boolean} frozen - true if table can have frozen columns
       */
      setFrozenTable: function (frozen) {
        if (frozen === this._frozenTable) {
          return;
        }
        this._frozenTable = frozen;
        // If the table should not be frozen, unfreeze all the columns
        if (!this._frozenTable) {
          this.setLeftFrozenColumns(0);
          this.setRightFrozenColumns(0);
          this.updateFrozenColumns();
          this.updateVisibleColumnsInDom();
        }
      },
      // ============== END - FROZEN COLUMNS FUNCTIONS =====================

      // ============== START - FOOTER/AGGREGATE FUNCTIONS ===================
      /**
       * Defines if the footer element is needed (used to display aggregate)
       * @param {boolean} b - true to display footer, false to hide it
       */
      setHasFooter: function (b) {
        if (this._hasFooter !== b) {
          this._hasFooter = b;
          cls.DOMElementHelper.toggleClass(this.getFooterGroupElement(), "hidden", !b);
        }
      },

      /**
       * Returns if table has a footer (used for aggregate)
       * @returns {boolean} true if footer is visible
       */
      hasFooter: function () {
        return this._hasFooter;
      },

      /**
       * Global text for aggregates
       * @param {string} text - global aggregate text
       */
      setAggregateGlobalText: function (text) {
        if (text !== "") {
          if (!this._aggregateGlobalTextElement) {
            this._aggregateGlobalTextElement = document.createElement("div");
            this._aggregateGlobalTextElement.classList.add("gbc_TableAggregateGlobalText");
          }
          this._aggregateGlobalTextElement.textContent = text;

          cls.DOMElementHelper.prependChild(this._footerAggregatesRowWidget.getElement(), this.getAggregateGlobalTextElement());
        }
      },

      /**
       * Opens the RowBound menu by delegating the action to the RowBoundDecoratorWidget.
       */
      openRowBoundMenu: function () {
        if (this.getCurrentRow() < 0) {
          return;
        }
        this.getRows()[this.getCurrentRow()].getRowBoundDecorator().openRowBoundMenu();
      },

      /**
       * Update all aggregates
       */
      updateAllAggregate: function () {
        if (this.hasFooter()) {
          for (const columnWidget of this.getColumns()) {
            columnWidget.setAggregate(null);
          }
        }
      },

      // ============== END - FOOTER/AGGREGATE FUNCTIONS ===================

      // ============== START - STYLE FUNCTIONS ===================
      /**
       * Show/hide table X grid
       * @param {boolean} showGridX - if true always show grid
       */
      setShowGridX: function (showGridX) {
        if (this._showGridX !== showGridX) {
          this._showGridX = showGridX;
          this.toggleClass("showGridX", Boolean(showGridX));
        }
      },

      /**
       * Show/hide table Y grid
       * @param {boolean} showGridY - if true always show grid
       */
      setShowGridY: function (showGridY) {
        if (this._showGridY !== showGridY) {
          this._showGridY = showGridY;
          this.toggleClass("showGridY", Boolean(showGridY));
        }
      },

      /**
       * Hide/Show column headers
       * @param {boolean} hidden - true if header must be hidden
       */
      setHeaderHidden: function (hidden) {
        if (this._headerHidden === hidden) {
          return;
        }
        this._headerHidden = hidden;
        this.toggleClass("headerHidden", Boolean(hidden));
      },

      /**
       * Set header columns alignment
       * @param {string} alignment - (left, center, right, auto)
       */
      setHeaderAlignment: function (alignment) {
        if (this._headerAlignment === alignment) {
          return;
        }
        this._headerAlignment = alignment;

        let cssAlignment = alignment;
        if (alignment === gbc.constants.headerAlignment.auto || alignment === gbc.constants.headerAlignment.default) {
          // if alignment is auto don't force specific alignement
          cssAlignment = null;
        }
        this.setStyle({ "--headerAlignement": cssAlignment });

        for (const columnElement of this.getColumns()) {
          columnElement.setTextAlign(alignment, true);
        }
      },

      /**
       * Get header columns alignment
       * @return {string} alignment - (left, center, right, auto)
       */
      getHeaderAlignment: function () {
        return this._headerAlignment;
      },

      /**
       * Set header columns position for flipped table
       * @param {string} position - (top, left)
       */
      setHeaderPosition: function (position) {
        if (this._headerPosition === position) {
          return;
        }

        this._headerPosition = position;
        this.toggleClasses(this._headerPosition === gbc.constants.headerPosition.top, "headerPosition--top");
        if (this.isFlipped()) {
          this.updateLayout();
        }
      },

      /**
       * Get header columns position
       * @return {string} position - (top, left)
       */
      getHeaderPosition: function () {
        return this._headerPosition;
      },

      /**
       * Set Default TTF color
       * @param {string} color - rgb formatted or css name
       */
      setDefaultTTFColor: function (color) {
        if (color === this._defaultTTFColor) {
          return;
        }
        this.setStyle(".gbc_RTableItemImage svg", {
          fill: color
        });
      },

      /**
       * Indicates if the last visible column should fill the empty space.
       * @param {boolean} fillEmptySpace - true if last column fills empty space
       */
      setResizeFillsEmptySpace: function (fillEmptySpace) {
        this._resizeFillsEmptySpace = fillEmptySpace;
      },

      /**
       * Indicates if the last visible column should fill the empty space.
       * @return {boolean} true if last column fills empty space
       * @publicdoc
       */
      isResizeFillsEmptySpace: function () {
        return this._resizeFillsEmptySpace;
      },

      /**
       * Update highlight row and cell
       */
      updateHighlight: function () {
        if (this.getCurrentRow() !== null) {
          this.getChild(this.getCurrentRow())?.setHighlight(
            this._highlightComponent.getShouldHighlightCurrentRow() && !this.isMultiRowSelectionEnabled()
          );
        }
        if (this._currentItem) {
          this._currentItem?.setHighlight(this._highlightComponent.getShouldHighlightCurrentCell() && !this.isMultiRowSelectionEnabled());
        }
      },

      // ============== END - STYLE FUNCTIONS ===================

      // ============== START - DOM ELEMENT GETTERS ===================
      /**
       * @inheritDoc
       */
      getScrollableArea: function () {
        return this.getContainerElement();
      },

      /**
       * Returns header group DOM Element
       * @returns {HTMLElement} header group DOM Element
       * @publicdoc
       */
      getHeaderGroupElement: function () {
        if (!this._headerGroupElement) {
          this._headerGroupElement = this.getElement().getElementsByClassName("gbc_TableHeaderGroup")[0];
        }
        return this._headerGroupElement;
      },

      /**
       * Returns footer group DOM Element
       * @returns {HTMLElement} footer group DOM Element
       * @publicdoc
       */
      getFooterGroupElement: function () {
        if (!this._footerGroupElement) {
          this._footerGroupElement = this.getElement().getElementsByClassName("gbc_TableFooterGroup")[0];
        }
        return this._footerGroupElement;
      },

      /**
       * Returns vertical scroller DOM Element
       * @returns {HTMLElement} scroller DOM Element
       * @publicdoc
       */
      getScrollerYElement: function () {
        if (!this._scrollerYElement) {
          this._scrollerYElement = this.getScrollableArea().getElementsByClassName("scroller_y")[0];
        }
        return this._scrollerYElement;
      },

      /**
       * Returns horizontal scroller DOM Element
       * @returns {HTMLElement} scroller DOM Element
       * @publicdoc
       */
      getScrollerXElement: function () {
        if (!this._scrollerXElement) {
          this._scrollerXElement = this.getScrollableArea().getElementsByClassName("scroller_x")[0];
        }
        return this._scrollerXElement;
      },

      /**
       * Returns aggregate global text DOM Element
       * @returns {HTMLElement} aggregate global text DOM Element
       */
      getAggregateGlobalTextElement: function () {
        return this._aggregateGlobalTextElement;
      }

      // ============== END - DOM ELEMENT GETTERS =====================
    };
  });
  cls.WidgetFactory.registerBuilder("RTable", cls.RTableWidget);
});
